/**
 * CommentsTab - Threaded comments for an annotation.
 */

import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { MessageSquare, Send, Trash2, Reply, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useComments, useAddComment, useDeleteComment } from '@/hooks/useAnnotations'
import type { Comment } from '@/types/annotation'

interface CommentsTabProps {
  annotationId: number
  studyId: string
}

export function CommentsTab({ annotationId, studyId }: CommentsTabProps) {
  const { toast } = useToast()

  // Comment state
  const [newComment, setNewComment] = useState('')
  const [replyingTo, setReplyingTo] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null)

  // Fetch comments
  const { data, isLoading, error } = useComments(studyId, annotationId)

  // Mutations
  const addComment = useAddComment(studyId, annotationId)
  const deleteComment = useDeleteComment(studyId, annotationId)

  const handleSubmit = async () => {
    if (!newComment.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Comment cannot be empty',
        variant: 'destructive',
      })
      return
    }

    try {
      await addComment.mutateAsync({
        content: newComment,
        parentCommentId: replyingTo || undefined,
      })
      setNewComment('')
      setReplyingTo(null)
    } catch {
      // Error handled by hook
    }
  }

  const handleDelete = async () => {
    if (deleteTarget === null) return

    try {
      await deleteComment.mutateAsync(deleteTarget)
      setDeleteTarget(null)
    } catch {
      // Error handled by hook
    }
  }

  const handleReply = (commentId: number) => {
    setReplyingTo(commentId)
  }

  const cancelReply = () => {
    setReplyingTo(null)
    setNewComment('')
  }

  // Build comment tree (group replies under parents)
  const buildCommentTree = (comments: Comment[]): Comment[] => {
    const commentMap = new Map<number, Comment & { replies?: Comment[] }>()
    const rootComments: (Comment & { replies?: Comment[] })[] = []

    // First pass: create map
    comments.forEach((comment) => {
      commentMap.set(comment.id, { ...comment, replies: [] })
    })

    // Second pass: build tree
    comments.forEach((comment) => {
      const mappedComment = commentMap.get(comment.id)!
      if (comment.parentCommentId) {
        const parent = commentMap.get(comment.parentCommentId)
        if (parent) {
          parent.replies = parent.replies || []
          parent.replies.push(mappedComment)
        } else {
          rootComments.push(mappedComment)
        }
      } else {
        rootComments.push(mappedComment)
      }
    })

    return rootComments
  }

  const getInitials = (name?: string) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const renderComment = (comment: Comment & { replies?: Comment[] }, depth = 0) => {
    const isReplying = replyingTo === comment.id

    return (
      <div key={comment.id} className={depth > 0 ? 'ml-8 mt-3' : ''}>
        <div className="flex gap-3">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarFallback className="text-xs">
              {getInitials(comment.creator?.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {comment.creator?.fullName || 'Unknown User'}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              </span>
            </div>
            <p className="text-sm">{comment.content}</p>
            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => handleReply(comment.id)}
              >
                <Reply className="h-3 w-3 mr-1" />
                Reply
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                onClick={() => setDeleteTarget(comment.id)}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Reply input */}
        {isReplying && (
          <div className="ml-11 mt-3 space-y-2">
            <Textarea
              placeholder={`Reply to ${comment.creator?.fullName || 'this comment'}...`}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="min-h-[80px]"
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={addComment.isPending || !newComment.trim()}
              >
                {addComment.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                <Send className="h-3 w-3 mr-1" />
                Reply
              </Button>
              <Button size="sm" variant="outline" onClick={cancelReply}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Render replies */}
        {comment.replies?.map((reply) => renderComment(reply, depth + 1))}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Failed to load comments
      </div>
    )
  }

  const comments = data?.comments || []
  const commentTree = buildCommentTree(comments)

  return (
    <div className="space-y-4">
      {/* New Comment Input (when not replying) */}
      {!replyingTo && (
        <div className="space-y-2">
          <Textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="min-h-[80px]"
          />
          <Button
            onClick={handleSubmit}
            disabled={addComment.isPending || !newComment.trim()}
            size="sm"
          >
            {addComment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Send className="h-4 w-4 mr-1" />
            Post Comment
          </Button>
        </div>
      )}

      {/* Comments List */}
      {commentTree.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No comments yet</p>
          <p className="text-sm text-muted-foreground">Be the first to comment</p>
        </div>
      ) : (
        <div className="space-y-4">
          {commentTree.map((comment) => renderComment(comment))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteComment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
